/**
 * Tax Planning Settings V2 API service.
 *
 * Promise-based wrapper around the backend endpoints under
 * `api/v1/TaxPlanningSettingsV2` (TaxPlanningSettingsV2Controller). Mirrors the
 * axios/base-URL/cookie-auth pattern used by `services/taxConfigV2Api.js` and
 * `services/taxPlanningSettingsApi.js`. All responses are the standard
 * ResponseModel `{ message, status, data }`; the helpers below unwrap `.data`
 * and throw on a non-success `status` (APIStatus.Successful === 0) so callers
 * can `await` the inner payload directly and `catch` a friendly Error.
 *
 * NOTE (JS project — no TypeScript): shapes documented via JSDoc typedefs below.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠️ SEMANTICS — read before wiring this to a screen
 *
 * This table is **VERSIONED** (many rows, one active) and it is a SEPARATE table
 * from V1's `tax.TaxPlanningSettings` — nothing here touches V1 behaviour.
 *
 * 1. `getSettings()` / `getVersion(id)` return the RAW row. A `null` field does
 *    NOT mean "empty": it means "no override stored — runtime uses the built-in
 *    default in the C# `TaxPromptDefaultsV2` (prompt copy) or
 *    `TaxPlanningConfigDefaultsV2` (LLM / node prompt / form behaviour)". Do not
 *    render `null` as an empty box on its own — prefill it with the built-in
 *    default (note 2) so an author always sees what the runtime actually uses.
 *
 * 2. `getPromptDefaults()` → `{ camelCaseKey: defaultText }` for the 39 prompt
 *    keys · `getConfigDefaults()` → `{ camelCaseKey: defaultValue }` for the 48
 *    config keys (mixed string / number / boolean / null). The settings dialog has
 *    NO "use default" switch any more — every field is always editable — so these
 *    two maps do two jobs instead:
 *      PREFILL  → on load, a column that is `null` is shown as its default value,
 *                 so even a brand-new version opens fully populated, not blank.
 *      COMPARE  → on save, a field whose value still equals its default (or that
 *                 was cleared to blank) is sent as `null` instead of as a stored
 *                 override. Emptying a box is how an author resets it to default.
 *    Both maps are pure code constants — version-independent, never persisted.
 *
 * 3. `saveSettings(body)` / `saveVersion(id, body)` are DIRECT-ASSIGN for the
 *    whole set (the backend never does `?? keep`). Therefore:
 *      • ALWAYS send every one of the 87 prompt+config fields — an omitted field
 *        is received as `null` and CLEARS that override.
 *      • Sending `null` is the intended way to reset a field back to default —
 *        it does NOT mean "leave unchanged".
 *    Use `TAX_PLANNING_SETTINGS_V2_ALL_KEYS` to build a complete payload.
 *    EXCEPTION — `versionCode` / `description` are version IDENTITY, not config:
 *    there `null`/blank DOES mean "leave unchanged" (versionCode is NOT NULL +
 *    unique so it can never be cleared; send `description: ''` to clear it).
 *
 * 4. 🔴 KEY SPELLING — ASP.NET Core's camelCase policy lowercases the leading
 *    run of capitals only while the NEXT char is also uppercase, so these four
 *    serialize with a trailing capital and MUST be spelled exactly like this:
 *        `modelID`  `modelDisplayID`  `yearID`  `topP`
 *    (not `modelId` / `modelDisplayId` / `yearId` / `topp`). Getting one wrong
 *    does not error — the value is silently dropped on save. Verified against
 *    `Model/AppModel/TaxPlanningSettingsV2Models.cs` (`TaxPlanningConfigDefaultsV2.Map()`).
 *
 * 5. 🔴 ERRORS — every version-CRUD failure comes back as HTTP **400** carrying a
 *    Thai `message` (the BAL cannot tell "not found" from "rejected"), NOT 404.
 *    Show `err.message` verbatim; never map an HTTP status to your own copy.
 *    The single exception is `getVersion(id)`, which does answer a true 404.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * @typedef {Object} TaxPlanningSettingsV2Dto  RAW settings of ONE version (null = use default)
 * @property {number} id
 * -- version identity --
 * @property {?string} versionCode            unique code, NOT NULL server-side
 * @property {?string} description
 * @property {boolean} isActive               true on exactly one version table-wide
 * -- state copy (greeting / resume / year question) --
 * @property {?string} startPromptTh          START_CONFIRM greeting; template — keeps the `{ปี}` placeholder
 * @property {?string} resumePromptTh         RESUME copy (form left in progress)
 * @property {?string} resumeGatePromptTh     classifier prompt behind the resume gate; MUST contain the
 *   literal `NEW` and `CONTINUE` (the node matches the reply on those substrings) or the backend ignores it
 * @property {?string} versionChoicePromptTh  VERSION CHOICE copy — a customer back on a FINISHED plan whose
 *   config/question version is no longer the active one; template — keeps the `{ปี}` placeholder. The two
 *   buttons («สร้างแผนภาษีใหม่» / «นำข้อมูลเดิมมาสร้างแผนภาษีใหม่») are code constants, sent as separate data
 * @property {?string} versionChoiceGatePromptTh  classifier prompt that reads a TYPED reply to that screen (a
 *   tapped button is matched exactly and never gets here); MUST contain the literal `NEW` and `REUSE` or the
 *   backend ignores it and uses the built-in prompt
 * @property {?string} yearQuestionPromptTh   SELECT_YEAR copy (tax-year picker)
 * @property {?string} noConfigMessageTh     copy while no question version is active (5 exits)
 * @property {?string} unavailableMessageTh  copy when the node throws (`bypassOnError` still wins)
 * @property {?string} historyResumeComposePromptTh  the instruction that welds `historyResumePromptTh` and the
 *   form's next pending question into ONE message. Fail-soft: no model / no pending question / LLM failure ships
 *   the welcome copy verbatim
 * @property {?string} completionRestartAskTh  the sentence that turns `completionMessageTh` into a QUESTION for
 *   the two prompts that read the built-in closing item. PROMPT-ONLY — the customer never sees it
 * @property {?string} agentFormRulesTh  the closing `[RULES]` block of `### Tax Planning Form (V2) ###` — the
 *   read-only briefing the node renders for ANOTHER agent. 🔴 No off switch: blank ⇒ the built-in rule, because
 *   without it a reading agent answers the form's questions or collects its fields, and that work reaches no
 *   writer of ours and is saved nowhere
 * @property {?string} completionRestartPromptTh  the extraction prompt for that closing question. 🔴 MUST contain
 *   the literal word `RESTART` — the branch reading the reply demands an exact match, so a text without it can
 *   only ever answer "no" and a customer asking to start again is stuck on a finished form
 * -- ASK stack (phraseQuestion) --
 * @property {?string} askGuardrailPromptTh   `### Ask Guardrail ###` — always-present floor
 * @property {?string} replyLanguagePromptTh  `### Reply Language ###` — LAST ask section; emitted only while
 *   `enableLanguageReplace` is on. Carries the `{{Language}}` token, substituted in every layer of the stack.
 * @property {?string} askFormDataCaptionTh  caption above `### Tax Planning Form Data ###` on the ASK stack —
 *   the terms of use for the customer's own saved rows. Also the text a CONFIRM checkpoint's caption is composed
 *   from, with `confirmFormDataClauseTh` appended on top.
 * @property {?string} confirmFormDataClauseTh  the ONE clause a CONFIRM checkpoint adds on top of
 *   `askFormDataCaptionTh` — "### Confirm Context ### is the only list you read back". Appended
 *   unconditionally: blank ⇒ the built-in clause, so no edit here or there leaves a checkpoint without it
 * @property {?string} clarifyRewriteRulesTh  the WHOLE `### Question Context ###` body of a CLARIFY (follow-up)
 *   turn — the follow-up framing plus the `[RULES]` block that says how to re-ask (never recite a ม.40(x) option
 *   set, draw the discriminating examples from `### §40 Income Signals ###`, never re-ask open-endedly). Two
 *   tokens: `{UNRESOLVED}` (MANDATORY — the deterministic re-ask; a text without it is refused and the built-in
 *   is used) and `{LANGUAGE}` (the reply language, same value `### Reply Language ###` is pinned to)
 * @property {?string} confirmSkipOfferRulesTh  the WHOLE `### Question Context ###` body of a CONFIRM_SKIP
 *   checkpoint — the offer to go back to skipped questions. Tokens `{SKIPPED}` (the list) and `{QUESTION}` (the
 *   authored text) are BOTH mandatory; a text missing either is refused in favour of the built-in
 * @property {?string} askedBackWithKnowledgeRulesTh  what to do when the customer asks a question BACK and
 *   `### Knowledge ###` will be in the prompt. Tokens `{CUSTOMER_QUESTION}` + `{QUESTION}`, both mandatory
 * @property {?string} askedBackNoKnowledgeRulesTh    the same turn with nothing retrieved — say so, then re-ask.
 *   Same two mandatory tokens. TWO columns on purpose: one wording that named an absent `### Knowledge ###`
 *   refused every customer who asked something back
 * @property {?string} calcResultLeadTh   lead-in above the SAVED figures inside `### Tax Planning Form Data ###`
 *   — the block that MAY be quoted
 * @property {?string} calcPreviewLeadTh  lead-in above the WHAT-IF figures — the block that may NOT
 * @property {?string} clarifyDefaultPhraseTh    clarify opener when the model parked one without a phrase
 * @property {?string} clarifyAboutLeadTh        word that names the line-item a clarify is about
 * @property {?string} clarifyChooseLeadTh       lead-in of the choices line — stored WITHOUT its trailing space
 * @property {?string} clarifyChooseSeparatorTh  separator between choices — stored WITHOUT its surrounding spaces
 *   (columns are trimmed on save, so the node owns the padding for both)
 * @property {?string} historyCaptionTh      caption above `### History ###` — ONE column for BOTH stacks
 *   (ask + extract); its "a restated answer is still an answer" clause is a parsing rule on the extract side
 * @property {?string} askKnowledgeCaptionTh  caption above `### Knowledge ###` on the ASK stack
 * @property {?string} phraseQuestionTaskTh   ⚰️ RETIRED 2026-08-03 — was `### Instruction ###` (normal question)
 * @property {?string} phraseConfirmTaskTh    ⚰️ RETIRED 2026-08-03 — was `### Instruction ###` (CONFIRM item)
 * -- EXTRACT stack (extractFields turn contract) --
 * @property {?string} extractContractPromptTh     `### Rules ###` — turn-contract JSON shape (stays last)
 * @property {?string} extractFocusIntentPromptTh  `### Focus Intent ###`
 * @property {?string} extractConfirmPromptTh      `### Confirm ###` — confirm_yes / confirm_edit
 * @property {?string} extractFormDataCaptionTh    caption above `### Tax Planning Form Data ###` on the EXTRACT
 *   stack — same snapshot as `askFormDataCaptionTh`, different contract (decide set/edit/del)
 * @property {?string} extractKnowledgeCaptionTh   caption above `### Knowledge ###` on the EXTRACT stack
 * @property {?string} section40SignalsPromptTh    `### §40 Income Signals ###`
 * @property {?string} incomeClassifyRulesTh       `### Income Classification ###` — token `{REF_LIST}`
 * @property {?string} subTypeMappingRulesTh       `### Income Sub-Types ###` — tokens `{CATALOG}` / `{SECTION_REF}`
 * -- node prompt stack (the 3 layers the node used to read from node.Data) --
 * @property {?string} nodeSystemPromptTh          BASE layer (was node.Data["systemPrompt"])
 * @property {?string} nodeQuestionSystemPromptTh  `### Tax Node Prompt ###` when phrasing a question
 * @property {?string} nodeAnswerSystemPromptTh    `### Tax Node Prompt ###` when extracting the answer
 * -- LLM config (was node.Data / BuildGptModelFromNodeDataAsync) --
 * @property {?string} provider @property {?string} modelName
 * @property {?number} modelID  @property {?number} modelDisplayID
 * @property {?number} temperature @property {?number} maxTokens @property {?number} topP
 * @property {?number} frequencyPenalty @property {?number} presencePenalty
 * @property {?string} stopSequence @property {?string} gender @property {?boolean} jsonResponse
 * @property {?string} timeZone @property {?boolean} enableTransactionId
 * @property {?number} memoryTurn @property {?number} memoryDuration @property {?number} shortTermMemory
 * @property {?boolean} longTermMemory @property {?string} apiVersion @property {?string} jsonRequest
 * @property {?number} nodeTimeout @property {?number} nodeMaxRetry
 * -- second model, ASK side only (identity columns; the rest is shared with the main model) --
 * @property {?boolean} useQuestionModel     off ⇒ one model does both jobs
 * @property {?string} questionProvider @property {?string} questionModelName
 * @property {?number} questionModelID  @property {?number} questionModelDisplayID
 * -- tax form behaviour --
 * @property {?string} yearMode               "active" | "pinned"
 * @property {?number} yearID
 * @property {?boolean} askWhenMultipleActive @property {?boolean} persist
 * @property {?string} completionMessageTh
 * -- voice form fill (POST /api/v1/LHB/VoiceFill — the SHORT_FORM card's speak-to-fill path) --
 * @property {?string} voiceFillPromptTh       blank ⇒ built-in VoiceFillPromptDefaults.SystemPromptTh
 * @property {?string} voiceFillProvider @property {?string} voiceFillModelName
 * @property {?number} voiceFillModelID  @property {?number} voiceFillModelDisplayID
 *   null model identity ⇒ voice fill reuses the node's own model
 * @property {?number} voiceFillTimeoutMs      soft ceiling on the LLM leg (default 8000 ms)
 * -- reply language (ASK side only: phraseQuestion / phraseClarify / quick-reply chips) --
 * @property {?boolean} enableLanguageReplace  off (default) = always Thai, byte-identical to before;
 *                                             on = detect the customer's language from their message
 *                                             and reply in it (twin of V1 `TaxPlanningSettings.EnableLanguageReplace`)
 * -- quick reply (chips under the question; authored AnswerOptions always win) --
 * @property {?string} quickReplySystemPromptTh  generator for the LLM FALLBACK only
 * @property {?string} quickReplyMode            "off" | "config" | "configThenLlm"
 * @property {?number} quickReplyMaxBubbles      caps the GENERATED list only (default 6)
 * -- output guard (the spell-checker that runs AFTER the model, on the text the customer will see) --
 * @property {?string} outputGuardRulesJson    JSON array of rules, run in order; blank ⇒ the built-in set in
 *   `TaxPromptDefaultsV2.OutputGuardRulesJson`, `[]` ⇒ every rule off. A rule is either regex
 *   (`find` + `replace` + `ignoreCase`) or built-in (`op`), each with a unique `id` and `enabled`
 * @property {?boolean} enableOutputGuard      master switch (default true) — off ships the model's text
 *                                             byte-identical, without touching the rules JSON
 * -- audit --
 * @property {?string} createdBy @property {?string} createdDate
 * @property {?string} updatedBy @property {?string} updatedDate
 *
 * @typedef {Object} TaxPlanningSettingsV2VersionListItem  row of the level-1 version list
 * @property {number} id
 * @property {?string} versionCode @property {?string} description
 * @property {boolean} isActive
 * @property {?string} updatedBy @property {?string} updatedDate
 *
 * @typedef {Object} SaveTaxPlanningSettingsV2Request
 * The 87 prompt+config fields of the DTO (no `id`, no audit) — direct-assign, send
 * all of them every time; `null` clears that override back to the built-in default.
 * Optionally `versionCode` / `description`, where null/blank means "leave unchanged".
 *
 * @typedef {Object} CreateTaxPlanningSettingsV2VersionRequest
 * @property {?string} versionCode  blank ⇒ backend generates `V{n}`
 * @property {?string} description
 * @property {?number} cloneFromId  null ⇒ start blank (pure code defaults)
 *
 * @typedef {Object} CloneTaxPlanningSettingsV2Request
 * @property {?string} versionCode  blank ⇒ backend generates `V{n}`
 * @property {?string} description
 *
 * @typedef {Object.<string,string>} TaxPromptDefaultsV2Map  39 entries, keys = the DTO prompt fields
 * @typedef {Object.<string,*>} TaxPlanningConfigDefaultsV2Map  48 entries, mixed value types (may be null)
 *
 * @typedef {Object} TaxSettingsSnapshot
 * One row of the «ประวัติการบันทึก» table = ONE successful save of a settings version.
 * @property {number} snapshotID @property {number} settingsID
 * @property {number} snapshotNo  the `v{N}` chip — 1-based, counted per settings version
 * @property {'SAVE'|'BASELINE'|'RESTORE'|'IMPORT'} sourceAction
 * @property {?number} restoredFromNo  the `snapshotNo` a RESTORE came from (null on every other action)
 * @property {?string} comment @property {?string} createdBy
 * @property {string} createdDate  🔴 no `Z` on the wire — render through `utils/thaiTime.js`
 * @property {boolean} isCurrent  highest `snapshotNo` = the version as it stands now (disable restore there)
 * @property {?string} payloadJson  the WHOLE settings row as a RAW JSON STRING — present only on
 *   `getSnapshot`, always null in the list. This service does NOT parse it: hand the string to
 *   `utils/taxSettingsSnapshotDiff.js` → `diffSettingsSnapshots(left, right)`.
 *
 * @typedef {Object} RestoreTaxSettingsSnapshotResult
 * @property {number} settingsID  ALWAYS the same version — a restore overwrites the config columns in
 *   place and never touches `id` / `versionCode` / `isActive` / the created-audit columns
 * @property {number} restoredFromNo
 * @property {string[]} warnings  Thai — e.g. a `modelID` that no longer exists here, or a column that
 *   arrived without its mandatory token. Never a reason to render the restore as failed.
 *
 * @typedef {Object} TaxSettingsBundleModelRef  ONE model slot, carried BY NAME so it survives the trip
 * @property {?string} provider @property {?string} modelName
 * @property {?string} botName  `mas.GPTModel.ModelName` of the row the id pointed at — `provider` +
 *   `modelName` alone cannot resolve the `modelID`/`modelDisplayID` pair (two different registries)
 *
 * @typedef {Object} TaxSettingsBundleSource  provenance of an export — DISPLAY ONLY
 * @property {?string} versionCode @property {?string} description
 *
 * @typedef {Object} TaxSettingsBundle  the export/import file. Not one integer id of the source database
 *   survives it: model wiring travels as `{provider, modelName, botName}` and the tax year as `taxYearCode`.
 * @property {string} kind  always `'lhb.taxsettings.v2.bundle'` — sniff this client-side before posting
 * @property {string} schemaVersion @property {string} exportedAt @property {?string} exportedBy
 * @property {TaxSettingsBundleSource} source
 * @property {Object} settings  the 87 config columns = 74 flat keys + `mainModel` / `questionModel` /
 *   `voiceFillModel` (`TaxSettingsBundleModelRef`) + `taxYearCode`. 🔴 `null` is DATA here ("no override —
 *   use the built-in default"), so never prefill a blank before sending the file back.
 *
 * @typedef {Object} TaxSettingsBundleUnresolvedModel
 * @property {string} slot  'main' | 'question' | 'voiceFill'
 * @property {?string} botName @property {?string} provider @property {?string} modelName
 *
 * @typedef {Object} ImportTaxSettingsBundleResult
 * @property {?number} id  the new version's id — `null` on a dry run (nothing was written)
 * @property {string} versionCode  the code actually used, after collision handling
 * @property {boolean} versionCodeAdjusted  true when a collision forced `V1` → `V1-2`
 * @property {boolean} dryRun
 * @property {TaxSettingsBundleUnresolvedModel[]} unresolvedModels  every model slot that matched nothing
 *   here — those columns are left `null` (= built-in default) rather than pointed at a guessed id
 * @property {string[]} warnings  Thai; includes the unresolved models and every column that arrived
 *   missing a mandatory token. The import still succeeded — show them anyway.
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_APP_API || 'http://localhost:5000/';

// Dedicated instance. `withCredentials` is set explicitly (cookie session auth)
// so it does not depend on the global axios default being applied first.
const http = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const API_STATUS_SUCCESS = 0; // APIStatus.Successful

/**
 * Unwrap a ResponseModel `{ message, status, data }` → inner `data`.
 * Throws an Error (with `.status`/`.data`) when the envelope reports failure.
 * Accepts either PascalCase (`Status`/`Data`) or camelCase (`status`/`data`).
 */
function unwrap(response) {
  const body = response && response.data;
  if (body && typeof body === 'object' && ('status' in body || 'Status' in body)) {
    const status = 'status' in body ? body.status : body.Status;
    const data = 'data' in body ? body.data : body.Data;
    if (status === API_STATUS_SUCCESS) {
      return data;
    }
    const err = new Error(body.message || body.Message || 'Request failed');
    err.status = status;
    err.data = data;
    throw err;
  }
  return body;
}

/**
 * Normalize an axios/transport error into a friendly Error carrying the API message.
 * The version-CRUD endpoints answer 400 + a Thai `message` for BOTH "not found" and
 * "rejected", so `message` — never `httpStatus` — is what the UI must render.
 */
function toError(error) {
  const body = error && error.response && error.response.data;
  const message =
    (body && (body.message || body.Message)) ||
    (error && error.message) ||
    'Request failed';
  const err = new Error(message);
  err.httpStatus = error && error.response && error.response.status;
  err.status = body && (body.status ?? body.Status);
  err.data = body && (body.data ?? body.Data);
  return err;
}

async function get(url) {
  try {
    return unwrap(await http.get(url));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function post(url, payload) {
  try {
    return unwrap(await http.post(url, payload));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function del(url) {
  try {
    return unwrap(await http.delete(url));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}

const ROOT = 'api/v1/TaxPlanningSettingsV2';

/**
 * The 43 editable PROMPT-COPY keys, in backend declaration order (state → ASK → EXTRACT → quick reply →
 * output guard → edit request).
 * 1:1 with the `getPromptDefaults()` map.
 * @type {string[]}
 */
export const TAX_PLANNING_SETTINGS_V2_PROMPT_KEYS = [
  // state copy
  'startPromptTh',
  'resumePromptTh',
  // HISTORY_RESUME — welcome copy for the isResume state read (Resume tapped in the app's Tax History);
  // the backend LLM-combines it with the form's next question, or sends it verbatim on any failure.
  'historyResumePromptTh',
  // Settings-backed since 2026-08-21 — the classifier behind the resume gate. 🔴 must keep the literal
  // words NEW and CONTINUE; the backend refuses a text missing either and falls back to the built-in prompt.
  'resumeGatePromptTh',
  // Settings-backed since 2026-09-29 — the VERSION CHOICE screen: a customer back on a FINISHED plan built on a
  // config/question version that is no longer active is asked «สร้างแผนภาษีใหม่ / นำข้อมูลเดิมมาสร้างแผนภาษีใหม่»
  // (the buttons are code constants, never typed here). versionChoicePromptTh = the copy (`{ปี}` placeholder);
  // versionChoiceGatePromptTh = the classifier for a TYPED reply. 🔴 the gate prompt must keep the literal words
  // NEW and REUSE; the backend refuses a text missing either and falls back to the built-in prompt.
  'versionChoicePromptTh',
  'versionChoiceGatePromptTh',
  'yearQuestionPromptTh',
  // Settings-backed since 2026-08-21 — the two node fallback bubbles. Copy only: nothing parses them and they
  // never reach an LLM, so `### Reply Language ###` cannot translate them.
  'noConfigMessageTh',
  'unavailableMessageTh',
  // ASK stack
  'askGuardrailPromptTh',
  // Settings-backed since 2026-08-12 — it used to be a hard-coded directive in TaxPromptStackV2 with no column
  // behind it. 🔴 Blanking it does NOT remove the section: like every other prompt key, blank falls back to the
  // built-in default. The off switch is the `enableLanguageReplace` toggle on the LLM tab.
  'replyLanguagePromptTh',
  // Settings-backed since 2026-08-20 — the caption of `### Tax Planning Form Data ###` on the ASK stack. It used
  // to be a const in TaxPromptStackV2 with no column behind it. Blank ⇒ the built-in caption, never a section
  // without terms of use over the customer's rows.
  'askFormDataCaptionTh',
  // Settings-backed since 2026-08-26 — the clause a CONFIRM checkpoint adds on top of the caption above. 🔴 It is
  // APPENDED, never substituted, and blank falls back to the built-in clause: neither this box nor the one above
  // can leave a checkpoint with the wide form snapshot sitting unlabelled beside its narrow recap.
  'confirmFormDataClauseTh',
  // Settings-backed since 2026-08-27 — the clarify [RULES] block, previously a string literal inside
  // PhraseClarifyAsync. 🔴 It is a TEMPLATE, not a caption: `{UNRESOLVED}` must survive any edit (the deterministic
  // re-ask reaches the model only through it) or the backend falls back to the built-in text and says so in the
  // turn log. `{LANGUAGE}` is optional.
  'clarifyRewriteRulesTh',
  // Settings-backed since 2026-08-27 — the last of the prompt surface that only a deployment could change.
  // 🔴 The four clarify words store NO surrounding whitespace (every column is trimmed on save); the node adds
  // the space after the choose-lead and around the separator, so both the built-in and a configured value go
  // through the identical concatenation.
  'clarifyDefaultPhraseTh',
  'clarifyAboutLeadTh',
  'clarifyChooseLeadTh',
  'clarifyChooseSeparatorTh',
  // 🔴 TEMPLATES with mandatory tokens — a text that loses one is refused and the built-in is used instead
  // (with a note in the turn log): {SKIPPED} + {QUESTION} here, {CUSTOMER_QUESTION} + {QUESTION} on the pair
  // below. The runtime substitutes into those slots and nowhere else.
  'confirmSkipOfferRulesTh',
  'askedBackWithKnowledgeRulesTh',
  'askedBackNoKnowledgeRulesTh',
  // 🔴 These two are the ONLY thing separating "figures you may quote" from "figures you may not". Wording that
  // blurs them is a wrong number read out to a customer, not a style regression.
  'calcResultLeadTh',
  'calcPreviewLeadTh',
  // General tab — the compose half of HISTORY_RESUME, whose copy half was already editable.
  'historyResumeComposePromptTh',
  // The built-in closing question: the sentence that makes it a question, and the prompt that reads the answer.
  // 🔴 completionRestartPromptTh must keep the literal word RESTART — see the JSDoc above.
  'completionRestartAskTh',
  'completionRestartPromptTh',
  // Settings-backed since 2026-08-27 — the last Thai instruction block the engine still built by hand, now
  // English like every other scaffolding. 🔴 APPENDED-ALWAYS, like confirmFormDataClauseTh: blank falls back to
  // the built-in rule rather than removing it. Three claims must survive any rewrite — do not answer the form's
  // questions for the customer, do not collect form data (hand them to the Tax Planning Agent), quote figures
  // digit for digit.
  'agentFormRulesTh',
  // Settings-backed since 2026-08-21. 🔴 historyCaptionTh backs the caption on BOTH stacks — one column, because
  // the section is emitted with no mode check and its rules are worded to hold for ask and extract alike.
  'historyCaptionTh',
  'askKnowledgeCaptionTh',
  // ⚰️ RETIRED as prompt inputs (2026-08-03) — the backend no longer emits `### Instruction ###`, and the two
  //    textareas were removed from TaxPlanningSettingsV2Dialog.vue. 🔴 THE KEYS MUST STAY IN THIS LIST.
  //    `saveVersion` is direct-assign: the dialog builds its payload by iterating
  //    TAX_PLANNING_SETTINGS_V2_ALL_KEYS, and any key it omits is received as `null` and CLEARS the column.
  //    Dropping these two here would silently delete text an admin typed before the retirement — and the
  //    columns are kept precisely so that text survives and the change stays reversible.
  'phraseQuestionTaskTh',
  'phraseConfirmTaskTh',
  // EXTRACT stack
  'extractContractPromptTh',
  'extractFocusIntentPromptTh',
  'extractConfirmPromptTh',
  // Twin of askFormDataCaptionTh, on the extract stack — same marker, different contract.
  'extractFormDataCaptionTh',
  'extractKnowledgeCaptionTh',
  'section40SignalsPromptTh',
  'incomeClassifyRulesTh',
  'subTypeMappingRulesTh',
  // quick reply — the LLM FALLBACK generator only (authored AnswerOptions always win)
  'quickReplySystemPromptTh',
  // Settings-backed since 2026-09-02 — the rules of `TaxOutputGuardV2`, the last pass over a reply before it
  // reaches the customer (and before it is written to Log_Message, so the log and the bubble stay the same
  // string). 🔴 It is JSON, not prompt copy — it never reaches an LLM; it lives on the PROMPT side only because
  // it is a text column whose blank falls back to the built-in set through the same `Effective()` as the rest.
  // Blank ⇒ the built-in rules, `[]` ⇒ every rule off (the master switch is `enableOutputGuard` below).
  'outputGuardRulesJson',
  // Settings-backed since 2026-09-03 — หมวด «ขอแก้ (Edit Request)»: 2 prompt ของเทิร์นพิเศษเมื่อลูกค้าขอแก้ข้อมูล
  // บนด่านยืนยันแต่ไม่ได้บอกว่าจะแก้อะไร. editRequestAskPromptTh ปรุงคำถามกลับ (token {CANDIDATES}/{LANGUAGE}/{ATTEMPT});
  // editRequestExtractPromptTh สกัดคำตอบว่าจะแก้ข้อไหน (token {CANDIDATE_REFS}/{CANDIDATES}). Blank ⇒ ใช้ default ใน
  // TaxPromptDefaultsV2. ดู config คู่กันใน TAX_PLANNING_SETTINGS_V2_CONFIG_KEYS ด้านล่าง.
  'editRequestAskPromptTh',
  'editRequestExtractPromptTh',
];

/**
 * The 57 CONFIG keys that live in settings — LLM (22) + node prompt stack (3) + tax form
 * behaviour (6) + ask model (5) + quick reply (2) + knowledge retrieval (2) + reply language (1)
 * + voice fill (6) + output guard (1) + edit request (9), in backend declaration order.
 * 1:1 with the `getConfigDefaults()` map.
 * (The first 38 are the ones that moved off `node.Data`; voice fill never lived there.)
 *
 * 🔴 `modelID` / `modelDisplayID` / `yearID` / `topP` carry a trailing capital on
 * purpose — see note 4 in the header. Do not "fix" them to `modelId` / `yearId`.
 * @type {string[]}
 */
export const TAX_PLANNING_SETTINGS_V2_CONFIG_KEYS = [
  // LLM
  'provider',
  'modelName',
  'modelID',
  'modelDisplayID',
  'temperature',
  'maxTokens',
  'topP',
  'frequencyPenalty',
  'presencePenalty',
  'stopSequence',
  'gender',
  'jsonResponse',
  'timeZone',
  'enableTransactionId',
  'memoryTurn',
  'memoryDuration',
  'shortTermMemory',
  'longTermMemory',
  'apiVersion',
  'jsonRequest',
  'nodeTimeout',
  'nodeMaxRetry',
  // second model, ASK side only (phrase question + quick replies) — identity columns only
  'useQuestionModel',
  'questionProvider',
  'questionModelName',
  'questionModelID',
  'questionModelDisplayID',
  // node prompt stack
  'nodeSystemPromptTh',
  'nodeQuestionSystemPromptTh',
  'nodeAnswerSystemPromptTh',
  // tax form behaviour
  'yearMode',
  'yearID',
  'askWhenMultipleActive',
  'persist',
  'streamingEnabled',
  // 🔴 MISSING until 2026-08-19, and the omission had teeth: `saveVersion` is direct-assign, so every press of
  //    บันทึก in the settings dialog sent this column as `null` and reset an admin's ask budget to the built-in 3.
  //    The column, the DTO, the request and all five BAL sites were there the whole time — only this list and the
  //    field descriptor were not, which is exactly the shape of bug this list exists to prevent.
  'clarifyAttemptLimit',
  'completionMessageTh',
  // reply language — ASK side only (phraseQuestion / phraseClarify / quick-reply chips);
  // off (default) leaves the assembled prompt byte-identical to before this field existed
  'enableLanguageReplace',
  // quick reply — off | config | configThenLlm, plus the cap on the GENERATED list
  'quickReplyMode',
  'quickReplyMaxBubbles',
  // knowledge retrieval — the two knobs of the ### Knowledge ### document search (both LLM ops).
  // `knowledgeTopN` replaces node.Data["taxPlanningKnowledgeTopN"], a key no screen ever wrote.
  // `knowledgeCosineSimilarity` is a SIMILARITY (0–1, higher = stricter); its default is `null`,
  // which means "no filter" — so an empty box here is the normal, shipped state, not a missing value.
  'knowledgeTopN',
  'knowledgeCosineSimilarity',
  // voice form fill — POST /api/v1/LHB/VoiceFill (the SHORT_FORM card's "fill it by speaking" path).
  // 🔴 It is NOT part of the node's prompt stack: the controller reads these columns and hands them to
  // VoiceFillBAL, which knows nothing about tax. They live here because this is where the versioning,
  // the admin editor and the playground's version pinning already are.
  // Blank prompt ⇒ the built-in VoiceFillPromptDefaults.SystemPromptTh; null model identity ⇒ voice fill
  // reuses the node's own model.
  'voiceFillPromptTh',
  'voiceFillProvider',
  'voiceFillModelName',
  // 🔴 NO editor for these two on purpose — the Voice Fill tab deliberately has no "บอทจาก Model Creator"
  //    picker (see the note there): the feature runs its own system prompt for a tiny extraction, so a bot's
  //    persona/knowledge buys nothing. They stay in this list so a save CARRIES THEM THROUGH unchanged
  //    (the dialog hydrates every key from the DTO and posts what it read, control or no control) — exactly
  //    how `phraseQuestionTaskTh` / `phraseConfirmTaskTh` survive. Verified by save round-trip 2026-08-15:
  //    values seeded through the API are still there after pressing บันทึก. Dropping them from this list is
  //    what would clear them, since an omitted key is received as null.
  'voiceFillModelID',
  'voiceFillModelDisplayID',
  'voiceFillTimeoutMs',
  // output guard — the master switch of `TaxOutputGuardV2` (default ON). Its twin `outputGuardRulesJson` sits on
  // the prompt list above; this one is a scalar the node branches on, so it is the switch to flip when the guard
  // must be turned off in a hurry — no JSON edit, and the admin's rules survive the outage untouched.
  'enableOutputGuard',
  // หมวด «ขอแก้ (Edit Request)» — เทิร์นพิเศษเมื่อลูกค้าขอแก้แต่ยังไม่บอกว่าจะแก้อะไร (คู่ prompt อยู่บนลิสต์ PROMPT
  // ด้านบน: editRequestAskPromptTh / editRequestExtractPromptTh). Default: enabled=true, attemptLimit=2,
  // useOwnModel=false (ปิด ⇒ ใช้ askModel เดิม). โมเดล/knowledge ของหมวดนี้แยกจากกลุ่ม ASK/EXTRACT ปกติ.
  'editRequestEnabled',
  'editRequestAttemptLimit',
  'editRequestUseOwnModel',
  'editRequestProvider',
  'editRequestModelName',
  'editRequestModelID',
  'editRequestModelDisplayID',
  'editRequestAskKnowledgeCategoriesJson',
  'editRequestExtractKnowledgeCategoriesJson',
];

/**
 * All 101 direct-assign columns (prompt copy + config). Iterate THIS when building a
 * save payload — a key you leave out is received as `null` and clears that override.
 * `versionCode` / `description` are deliberately NOT in here: they are identity, not
 * config, and follow "null = leave unchanged" instead.
 * @type {string[]}
 */
export const TAX_PLANNING_SETTINGS_V2_ALL_KEYS = [
  ...TAX_PLANNING_SETTINGS_V2_PROMPT_KEYS,
  ...TAX_PLANNING_SETTINGS_V2_CONFIG_KEYS,
];

export const taxPlanningSettingsV2Api = {
  // ── legacy singleton-shaped endpoints (now = "the ACTIVE version") ─────────────

  /**
   * Read the ACTIVE settings version (get-or-create server-side).
   * RAW values — a `null` field means "no override, runtime uses the built-in
   * default", NOT "empty". Pair with `getPromptDefaults()` / `getConfigDefaults()`.
   * @returns {Promise<TaxPlanningSettingsV2Dto>}
   */
  getSettings() {
    return get(`${ROOT}`);
  },

  /**
   * Save the ACTIVE settings version — DIRECT-ASSIGN of the whole set. Every one of
   * the 87 prompt+config fields must be present in `body`: an omitted or `null` field
   * CLEARS that override and falls back to the built-in default.
   * @param {SaveTaxPlanningSettingsV2Request} body complete payload (see the key lists above)
   * @returns {Promise<TaxPlanningSettingsV2Dto>} the saved (still RAW) settings
   */
  saveSettings(body) {
    return post(`${ROOT}/save`, body);
  },

  /**
   * Built-in default prompt texts — `{ camelCaseKey: defaultText }`, 39 keys.
   * The dialog PREFILLS these into the editor wherever the stored column is `null`,
   * then COMPARES against them on save: a field left equal to its default — or
   * cleared to blank — goes back as `null` instead of as a stored override.
   * Pure code constants — version-independent.
   * @returns {Promise<TaxPromptDefaultsV2Map>}
   */
  getPromptDefaults() {
    return get(`${ROOT}/prompt-defaults`);
  },

  /**
   * Built-in default CONFIG values — `{ camelCaseKey: defaultValue }`, 48 keys,
   * mixed types (string / number / boolean / null). Same role as
   * `getPromptDefaults()` but for the LLM / node prompt / form-behaviour columns:
   * prefill the editor when the column is `null`, then send `null` back for every
   * field the author left at its default (emptying a box = reset to default).
   * ⚠️ 12 of these defaults ARE themselves `null` (`modelDisplayID`, `maxTokens`,
   * `frequencyPenalty`, `presencePenalty`, `stopSequence`, `timeZone`, `apiVersion`,
   * `jsonRequest`, `questionProvider`, `questionModelName`, `questionModelDisplayID`,
   * `completionMessageTh`) — those fields legitimately render EMPTY, and `apiVersion`
   * / `jsonRequest` / `maxTokens` must stay null so the runtime `GetDDLModelData`
   * fallback still fires. Pure code constants — version-independent.
   * @returns {Promise<TaxPlanningConfigDefaultsV2Map>}
   */
  getConfigDefaults() {
    return get(`${ROOT}/config-defaults`);
  },

  // ── versioning (level 1 = list, level 2 = per-version editor) ──────────────────

  /**
   * Every settings version — identity + audit only, no prompt/config payload.
   * Never empty: the backend seeds/promotes one version first.
   * @returns {Promise<TaxPlanningSettingsV2VersionListItem[]>}
   */
  listVersions() {
    return get(`${ROOT}/versions`);
  },

  /**
   * One version, RAW (null column = "using the built-in default").
   * Read-only — unlike `getSettings()` it never creates a row.
   * ⚠️ The one endpoint here that answers a true 404 (`err.httpStatus === 404`).
   * @param {number} id
   * @returns {Promise<TaxPlanningSettingsV2Dto>}
   */
  getVersion(id) {
    return get(`${ROOT}/versions/${id}`);
  },

  /**
   * Create a version. Blank `versionCode` ⇒ backend generates `V{n}`; `cloneFromId`
   * set ⇒ deep-copy that version's columns, otherwise start blank (= pure code
   * defaults). Always created INACTIVE — activate it explicitly.
   * @param {CreateTaxPlanningSettingsV2VersionRequest} body
   * @returns {Promise<TaxPlanningSettingsV2Dto>} the created version (isActive:false)
   */
  createVersion(body) {
    return post(`${ROOT}/versions`, body || {});
  },

  /**
   * Save ONE version (level 2 of the dialog) — same direct-assign contract as
   * `saveSettings`. Does NOT change which version is active.
   * @param {number} id
   * @param {SaveTaxPlanningSettingsV2Request} body complete payload
   * @returns {Promise<TaxPlanningSettingsV2Dto>}
   */
  saveVersion(id, body) {
    return post(`${ROOT}/versions/${id}/save`, body);
  },

  /**
   * Make this THE active version (the backend deactivates every other one first).
   * ⚠️ Takes NO body — the id in the route is the whole request.
   * @param {number} id
   * @returns {Promise<TaxPlanningSettingsV2Dto>} the activated version
   */
  activateVersion(id) {
    return post(`${ROOT}/versions/${id}/activate`);
  },

  /**
   * Duplicate a version (every prompt + config column) under a new unique code.
   * The clone always starts INACTIVE, so duplicating the live config can never
   * silently switch what the runtime uses.
   * @param {number} id source version
   * @param {CloneTaxPlanningSettingsV2Request} [body] blank code ⇒ generated
   * @returns {Promise<TaxPlanningSettingsV2Dto>} the new version (isActive:false)
   */
  cloneVersion(id, body) {
    return post(`${ROOT}/versions/${id}/clone`, body || {});
  },

  /**
   * Delete a version. The backend REJECTS the active version and the last remaining
   * one — both arrive as HTTP 400 with a Thai `message` meant to be shown verbatim.
   * @param {number} id
   * @returns {Promise<null>}
   */
  deleteVersion(id) {
    return del(`${ROOT}/versions/${id}`);
  },

  // ── Save history (snapshots of one settings version) ──────────────────────────

  /**
   * The save-history of one settings version, newest (`isCurrent: true`) first.
   *
   * The rows come back WITHOUT `payloadJson` (it is null on every one) — one snapshot is the whole
   * prompt set, so shipping 50 of them would mean shipping every prompt 50 times. Fetch the one you
   * need with `getSnapshot`. A version nobody has saved yet answers `[]` (200, not 404).
   * @param {number} versionId  `TaxPlanningSettingsV2Dto.id`
   * @returns {Promise<TaxSettingsSnapshot[]>}
   */
  listHistory(versionId) {
    return get(`${ROOT}/versions/${versionId}/history`);
  },

  /**
   * One snapshot WITH its `payloadJson` — the whole settings row as a raw JSON string. It stays a
   * STRING here on purpose; parse it in the dialog, or hand both strings straight to
   * `utils/taxSettingsSnapshotDiff.js` → `diffSettingsSnapshots(left, right)`.
   * @param {number} snapshotId
   * @returns {Promise<TaxSettingsSnapshot>} 404 → rejected Error
   */
  getSnapshot(snapshotId) {
    return get(`${ROOT}/history/${snapshotId}`);
  },

  /**
   * Restore a snapshot over the CURRENT columns of its own settings version. The version `id`,
   * `versionCode`, `isActive` and created-audit are untouched, and a column the snapshot holds as
   * `null` is restored AS null — "use the built-in default" is stored state, not a blank to fill in.
   * The restore is itself recorded as a new snapshot, so it can be rolled back again.
   *
   * Reload the version into the editor afterwards, or the open form still shows the pre-restore values.
   * @param {number} snapshotId
   * @param {?{comment?:?string}} [body] optional note (defaults server-side to `Restored from v{N}`)
   * @returns {Promise<RestoreTaxSettingsSnapshotResult>} `warnings` may be non-empty on a SUCCESSFUL restore
   */
  restoreSnapshot(snapshotId, body) {
    return post(`${ROOT}/history/${snapshotId}/restore`, body || {});
  },

  // ── Export / Import (move a settings version between machines) ────────────────

  /**
   * Export one settings version as a portable bundle — RAW columns, `null`s included.
   *
   * Returns the bundle OBJECT: the endpoint answers with a normal `ResponseModel`, NOT a file
   * download. The browser is what turns it into a `.json` (Blob + `<a download>`, and defer
   * `URL.revokeObjectURL` with `setTimeout(…, 0)` or Safari cancels the download).
   * @param {number} versionId
   * @returns {Promise<TaxSettingsBundle>} 404 → rejected Error
   */
  exportVersion(versionId) {
    return get(`${ROOT}/versions/${versionId}/export`);
  },

  /**
   * Import a bundle as a NEW settings version. The new version is ALWAYS created inactive, so an
   * import can never switch what the runtime is using.
   *
   * A settings version is not bound to a tax config version, so — unlike the Question / Calculate
   * imports — there is no destination id to send: the file plus an optional code is the whole request.
   * Model slots are resolved BY NAME on this machine; anything that matches nothing lands in
   * `unresolvedModels` + `warnings` with the column left `null`, and the import still succeeds.
   * @param {Object} args
   * @param {TaxSettingsBundle} args.bundle  the parsed file. Untrusted input — only its content is used;
   *   `bundle.source.*` is display-only.
   * @param {?string} [args.versionCode]  blank ⇒ the file's `source.versionCode`. Either way a collision
   *   auto-increments (`V1` → `V1-2`) and the result reports `versionCodeAdjusted: true`.
   * @param {?string} [args.description]  blank ⇒ the file's `source.description`.
   * @param {boolean} [args.dryRun=false]  true ⇒ resolve everything and report the preview + warnings but
   *   write NOTHING to the database — this is what powers the preview panel; `id` comes back `null`.
   * @returns {Promise<ImportTaxSettingsBundleResult>}
   */
  importBundle({ bundle, versionCode, description, dryRun } = {}) {
    return post(`${ROOT}/import`, {
      bundle,
      versionCode: versionCode || null,
      description: description || null,
      dryRun: Boolean(dryRun),
    });
  },
};

export default taxPlanningSettingsV2Api;
